import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useLocation } from "react-router";
import auth from "../utils/auth";
import { api } from "../utils/api";
import { track } from "../utils/track";

/**
 * 만족도 묻기 (2026-10-09 사장님): 악보를 3곡 이상 본 날, 악보 화면에서 나올 때 한 번.
 * 한 사람에게 90일에 한 번('나중에'를 눌러도 90일 뒤 — 2026-10-10 사장님). 연주 중에는 안 뜸(악보 화면을 벗어날 때만).
 */
const KEY_DAY = "fb_day";       // {d:'YYYY-MM-DD', n: 오늘 본 악보 수}
const KEY_NEXT = "fb_next";     // 다음에 물어도 되는 시각(ms)
const DAY = 24 * 60 * 60 * 1000;

const get = (k: string) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
const set = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch (e) { /* 저장 못 해도 그냥 */ } };
const today = () => new Date().toISOString().slice(0, 10);
const isScore = (p: string) => /^\/notes\/\d+/.test(p);

const FeedbackPrompt: React.FC = () => {
  const { pathname } = useLocation();
  const prev = useRef(pathname);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [done, setDone] = useState(false);

  const first = useRef(true);
  useEffect(() => {
    const was = prev.current;
    prev.current = pathname;
    const isFirst = first.current;
    first.current = false;
    if (isScore(pathname) && (pathname !== was || isFirst)) {
      let d = { d: today(), n: 0 };
      try { const v = JSON.parse(get(KEY_DAY) || "null"); if (v && v.d === today()) d = v; } catch (e) { /* 새로 */ }
      d.n += 1;
      set(KEY_DAY, JSON.stringify(d));
      return;
    }
    if (isScore(was) && !isScore(pathname) && auth.getToken()) {
      let n = 0;
      try { const v = JSON.parse(get(KEY_DAY) || "null"); if (v && v.d === today()) n = v.n; } catch (e) { /* 0 */ }
      const next = Number(get(KEY_NEXT) || 0);
      if (n >= 3 && Date.now() >= next) {
        setOpen(true);
        track("feedback:show");
      }
    }
  }, [pathname]);

  const later = () => {
    set(KEY_NEXT, String(Date.now() + 90 * DAY));
    setOpen(false);
    track("feedback:later");
  };
  const send = async () => {
    if (!rating) return;
    set(KEY_NEXT, String(Date.now() + 90 * DAY));
    setDone(true);
    track(`feedback:send-${rating}`);
    try { await api.post("/feedback", { rating, text: text.trim() }); } catch (e) { /* 못 보내도 다시 묻지 않음 */ }
    setTimeout(() => setOpen(false), 1600);
  };

  if (!open) return null;
  return (
    <Back onClick={later}>
      <Box role="dialog" aria-modal="true" aria-labelledby="fb-title" onClick={(e) => e.stopPropagation()}>
        {done ? (
          <p className="thanks">고마워요! 보내 주신 의견으로 더 좋게 만들게요.</p>
        ) : (
          <>
            <h2 id="fb-title">코드스코어, 쓸 만하세요?</h2>
            <Stars role="radiogroup" aria-label="별점">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} type="button" role="radio" aria-checked={rating === i} aria-label={`${i}점`} className={i <= rating ? "on" : ""} onClick={() => setRating(i)}>★</button>
              ))}
            </Stars>
            <textarea
              id="fb-text"
              value={text}
              maxLength={1000}
              onChange={(e) => setText(e.target.value)}
              placeholder="더 좋아지려면? 불편한 점이나 바라는 기능을 한 줄로 적어 주세요 (선택)"
              aria-label="의견"
            />
            <div className="btns">
              <button type="button" className="ghost" onClick={later}>나중에</button>
              <button type="button" className="primary" disabled={!rating} onClick={send}>보내기</button>
            </div>
          </>
        )}
      </Box>
    </Back>
  );
};

export default FeedbackPrompt;

const Back = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  @media (min-width: 700px) { align-items: center; }
`;

const Box = styled.div`
  width: 100%;
  max-width: 420px;
  box-sizing: border-box;
  padding: 22px 20px 18px;
  border-radius: 18px;
  background: #171a21;
  color: #f3f4f6;
  font-family: "Noto Sans KR", "Apple SD Gothic Neo", sans-serif;
  & > h2 { margin: 0 0 12px; font-size: 18px; font-weight: 700; text-align: center; }
  & > textarea {
    width: 100%;
    box-sizing: border-box;
    min-height: 78px;
    margin-top: 14px;
    padding: 10px 12px;
    border-radius: 12px;
    border: 1px solid #2d323d;
    background: #0f1115;
    color: #f3f4f6;
    font: inherit;
    font-size: 14px;
    resize: vertical;
  }
  & > .btns { display: flex; gap: 8px; margin-top: 14px; }
  & > .btns > button { flex: 1; height: 46px; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; }
  & .ghost { border: 1px solid #2d323d; background: none; color: #a3a9b5; }
  & .primary { border: 0; background: #5b8cff; color: #fff; }
  & .primary:disabled { opacity: 0.45; cursor: default; }
  & > .thanks { margin: 8px 0; text-align: center; font-size: 15px; line-height: 1.6; }
`;

const Stars = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  & > button {
    width: 46px;
    height: 46px;
    border: 0;
    background: none;
    font-size: 34px;
    line-height: 1;
    color: #3a3f4b;
    cursor: pointer;
  }
  & > button.on { color: #ffb547; }
  & > button:focus-visible { outline: 2px solid #5b8cff; border-radius: 8px; }
`;
